import { PrimaryValueIcon } from '@/components/PrimaryValueIcon';
import { ExerciseConfigExecutionTypeEnum, ExerciseDTO } from '@/generated';
import { FitnessCenter } from '@mui/icons-material';
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

export const ExerciseMetrics = (props: { exercise: ExerciseDTO }) => {
  const config = props.exercise.config;
  const executionType = config.executionType ?? (config.useTimer ? ExerciseConfigExecutionTypeEnum.Duration : ExerciseConfigExecutionTypeEnum.Reps);
  const firstInterval = config.timing?.intervals[0];
  const intervals = config.timing?.intervals ?? [];
  const isDuration = executionType === ExerciseConfigExecutionTypeEnum.Duration;
  const isIntervals = executionType === ExerciseConfigExecutionTypeEnum.Intervals;
  const primaryIcon = <PrimaryValueIcon executionType={executionType} />;
  const primaryValue = isIntervals ? (firstInterval ? `${firstInterval.durationSeconds}/${firstInterval.pauseAfterSeconds ?? 0}` : '-') : (config.value ?? '-');

  const tooltip = (
    <Stack spacing={0.5} sx={{ p: 0.5 }}>
      <Typography variant="subtitle2">Mode: {isDuration ? 'Duration' : isIntervals ? 'Intervals' : 'Reps'}</Typography>
      {!isIntervals && (
        <Typography variant="body2">
          {isDuration ? 'Duration' : 'Reps'}: {primaryValue}
          {isDuration ? ' seconds' : ''}
        </Typography>
      )}
      <Typography variant="body2">Weight: {config.weight ?? '-'}</Typography>
      {isIntervals && <Typography variant="body2">Init time: {config.timing?.initTimeSeconds ?? '-'} seconds</Typography>}
      {isIntervals && props.exercise.isUniLateral && config.trackPerSide && (
        <Typography variant="body2">Switch time: {config.timing?.switchPauseSeconds ?? '-'} seconds</Typography>
      )}
      {isIntervals && intervals.length === 1 && (
        <Typography variant="body2">
          Duration: {firstInterval?.durationSeconds ?? '-'} seconds, Pause: {firstInterval?.pauseAfterSeconds ?? 0} seconds
        </Typography>
      )}
      {isIntervals && intervals.length > 1 && (
        <Table size="small" sx={{ '& .MuiTableCell-root': { color: 'inherit', borderColor: 'rgba(255,255,255,0.25)', px: 1, py: 0.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell align="right">Pause</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {intervals.map((interval, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell align="right">{interval.durationSeconds}s</TableCell>
                <TableCell align="right">{interval.pauseAfterSeconds ?? 0}s</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );

  return (
    <Tooltip title={tooltip} arrow>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 'fit-content' }}>
        <Metric icon={primaryIcon} value={primaryValue} maxCharacters={8} />
        <Metric icon={<FitnessCenter fontSize="small" />} value={config.weight ?? '-'} maxCharacters={3} />
      </Stack>
    </Tooltip>
  );
};

const Metric = (props: { icon: React.ReactNode; value: string | number; maxCharacters: number }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary" sx={{ minWidth: 54 }}>
    <Box sx={{ display: 'flex', width: 24, flexShrink: 0 }}>{props.icon}</Box>
    <Typography variant="body2" noWrap sx={{ maxWidth: `${props.maxCharacters}ch`, textOverflow: 'ellipsis' }}>
      {props.value}
    </Typography>
  </Stack>
);
